import React, { useState } from 'react';
import { 
  FaExchangeAlt, 
  FaUndoAlt, 
  FaCreditCard, 
  FaTruck, 
  FaClock, 
  FaCheckCircle,
  FaTimesCircle,
  FaQuestionCircle,
  FaEnvelope,
  FaPhoneAlt,
  FaHome,
  FaPrint,
  FaDownload,
  FaGem,
  FaShieldAlt,
  FaGift,
  FaBoxOpen,
  FaMoneyBillWave
} from 'react-icons/fa';

const ReturnsPolicy = () => {
  const [lastUpdated] = useState('April 15, 2026');

  const returnSteps = [
    {
      step: 1,
      title: "Initiate Return",
      description: "Contact our customer support within 30 days of receiving your order.",
      icon: <FaExchangeAlt className="w-6 h-6" />,
      details: "Email us at info@skkjewellers.com or call +91 92856 68748 with your order number"
    },
    {
      step: 2,
      title: "Get Approval",
      description: "Receive return authorization and shipping instructions.",
      icon: <FaCheckCircle className="w-6 h-6" />,
      details: "We'll provide you with a return authorization number and return address"
    },
    {
      step: 3,
      title: "Pack & Ship",
      description: "Pack the item securely in original packaging.",
      icon: <FaBoxOpen className="w-6 h-6" />,
      details: "Include all original tags, certificates, and accessories"
    },
    {
      step: 4,
      title: "Quality Check",
      description: "We inspect the returned item within 3-5 business days.",
      icon: <FaShieldAlt className="w-6 h-6" />,
      details: "Item must be unused, unworn, and in original condition"
    },
    {
      step: 5,
      title: "Refund Processed",
      description: "Refund issued to original payment method.",
      icon: <FaMoneyBillWave className="w-6 h-6" />,
      details: "Refunds take 7-10 business days to appear in your account"
    }
  ];

  const eligibleItems = [
    { text: "Unused and unworn jewelry", condition: "No signs of wear, scratches, or damage" },
    { text: "Original packaging", condition: "Box, certificates, tags, and accessories included" },
    { text: "Within 30 days", condition: "From date of delivery" },
    { text: "Proof of purchase", condition: "Order number or receipt required" }
  ];

  const nonReturnableItems = [
    { text: "Customized jewelry", reason: "Made-to-order with personal engravings" },
    { text: "Pierced earrings", reason: "Hygiene reasons" },
    { text: "Final sale items", reason: "Clearance or discounted over 50%" },
    { text: "Gift cards", reason: "Cannot be returned or exchanged" },
    { text: "Damaged by customer", reason: "Physical damage, improper care" }
  ];

  const refundTimeline = [
    { type: "Original Payment", time: "7-10 business days", method: "Credit Card / Debit Card" },
    { type: "Store Credit", time: "2-3 business days", method: "Gift Card / Store Credit" },
    { type: "Exchange", time: "5-7 business days", method: "New item shipped" },
    { type: "Cash on Delivery", time: "10-15 business days", method: "Bank Transfer / Cheque" }
  ];

  const faqs = [
    {
      question: "How do I start a return?",
      answer: "Contact our customer support team via email or phone with your order number. We'll guide you through the process and provide a return authorization."
    },
    {
      question: "Who pays for return shipping?",
      answer: "If the return is due to our error (wrong item, defective product), we provide free return shipping. For other returns, the customer is responsible for return shipping costs."
    },
    {
      question: "Can I exchange an item?",
      answer: "Yes! We offer free exchanges for size or design changes. Contact us within 30 days to arrange an exchange."
    },
    {
      question: "What if my item arrives damaged?",
      answer: "Please contact us immediately with photos of the damage. We'll arrange a replacement or full refund at no cost to you."
    },
    {
      question: "How long do refunds take?",
      answer: "Once we receive and inspect your return, refunds are processed within 3-5 business days. It may take 7-10 business days for the refund to appear in your account."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-amber-50">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-purple-700 via-pink-700 to-amber-700 text-white">
        <div className="container mx-auto px-4 py-8 md:py-12">
          {/* Navigation Links */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <a href="/" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
                <FaHome className="w-4 h-4" />
                <span className="text-sm">Home</span>
              </a>
              <span className="text-white/40">/</span>
              <span className="text-sm font-medium">Returns Policy</span>
            </div>
            <div className="flex gap-3">
              <button 
                onClick={() => window.print()}
                className="flex items-center gap-2 px-3 py-1.5 bg-white/20 rounded-lg hover:bg-white/30 transition-all text-sm"
              >
                <FaPrint className="w-3.5 h-3.5" />
                Print
              </button>
              <button className="flex items-center gap-2 px-3 py-1.5 bg-white/20 rounded-lg hover:bg-white/30 transition-all text-sm">
                <FaDownload className="w-3.5 h-3.5" />
                Download PDF
              </button>
            </div>
          </div>

          {/* Main Header */}
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-4">
              <FaUndoAlt className="w-4 h-4" />
              <span className="text-sm font-semibold">30 Day Returns</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3">
              Returns & Refunds
            </h1>
            <p className="text-purple-100 max-w-2xl mx-auto text-sm md:text-base">
              We want you to love your jewelry. If you're not completely satisfied, we're here to help.
            </p>
            <div className="mt-4 text-sm text-purple-200">
              Last Updated: {lastUpdated}
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 md:py-12 max-w-6xl">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 text-center shadow-md border border-purple-100">
            <div className="w-12 h-12 bg-gradient-to-r from-purple-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <FaClock className="w-6 h-6 text-purple-600" />
            </div>
            <h4 className="font-bold text-gray-800 text-lg">30 Days</h4>
            <p className="text-xs text-gray-500">To initiate return</p>
          </div>
          <div className="bg-white rounded-xl p-4 text-center shadow-md border border-purple-100">
            <div className="w-12 h-12 bg-gradient-to-r from-purple-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <FaCheckCircle className="w-6 h-6 text-purple-600" />
            </div>
            <h4 className="font-bold text-gray-800 text-lg">Free Returns</h4>
            <p className="text-xs text-gray-500">For defective items</p>
          </div>
          <div className="bg-white rounded-xl p-4 text-center shadow-md border border-purple-100">
            <div className="w-12 h-12 bg-gradient-to-r from-purple-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <FaMoneyBillWave className="w-6 h-6 text-purple-600" />
            </div>
            <h4 className="font-bold text-gray-800 text-lg">Full Refund</h4>
            <p className="text-xs text-gray-500">To original payment</p>
          </div>
          <div className="bg-white rounded-xl p-4 text-center shadow-md border border-purple-100">
            <div className="w-12 h-12 bg-gradient-to-r from-purple-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <FaGem className="w-6 h-6 text-purple-600" />
            </div>
            <h4 className="font-bold text-gray-800 text-lg">Easy Exchange</h4>
            <p className="text-xs text-gray-500">Size or design</p>
          </div>
        </div>

        {/* Return Process */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden border border-purple-100 mb-8">
          <div className="p-6 md:p-8 border-b border-gray-200 bg-gradient-to-r from-purple-50/30 to-pink-50/30">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center">
                <FaUndoAlt className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                How to Return an Item
              </h2>
            </div>
            <p className="text-gray-600 text-sm">
              Follow these simple steps to return your jewelry and get a refund or exchange.
            </p>
          </div>

          <div className="p-6 md:p-8">
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-purple-500 via-pink-500 to-amber-500 hidden md:block"></div>
              
              {returnSteps.map((step, idx) => (
                <div key={step.step} className="relative flex flex-col md:flex-row gap-4 mb-8 last:mb-0">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center text-white font-bold z-10 relative">
                      {step.step}
                    </div>
                  </div>
                  <div className="flex-1 bg-purple-50/50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="text-purple-600">
                        {step.icon}
                      </div>
                      <h3 className="font-semibold text-gray-800">{step.title}</h3>
                    </div>
                    <p className="text-gray-600 text-sm mb-1">{step.description}</p>
                    <p className="text-gray-500 text-xs">{step.details}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Eligible for Return */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden border border-purple-100">
            <div className="p-5 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-100">
              <div className="flex items-center gap-2">
                <FaCheckCircle className="w-5 h-5 text-green-600" />
                <h3 className="font-bold text-gray-800">Eligible for Return</h3>
              </div>
            </div>
            <div className="p-5">
              <ul className="space-y-3">
                {eligibleItems.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <FaCheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-gray-800">{item.text}</p>
                      <p className="text-xs text-gray-500">{item.condition}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Non-Returnable Items */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden border border-purple-100">
            <div className="p-5 bg-gradient-to-r from-red-50 to-rose-50 border-b border-red-100">
              <div className="flex items-center gap-2">
                <FaTimesCircle className="w-5 h-5 text-red-600" />
                <h3 className="font-bold text-gray-800">Non-Returnable Items</h3>
              </div>
            </div>
            <div className="p-5">
              <ul className="space-y-3">
                {nonReturnableItems.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <FaTimesCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-gray-800">{item.text}</p>
                      <p className="text-xs text-gray-500">{item.reason}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Refund Timeline */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden border border-purple-100 mb-8">
          <div className="p-6 md:p-8 border-b border-gray-200 bg-gradient-to-r from-purple-50/30 to-pink-50/30">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center">
                <FaClock className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                Refund Timeline
              </h2>
            </div>
            <p className="text-gray-600 text-sm">
              Once we receive and inspect your return, here's when you can expect your refund.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Refund Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Processing Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {refundTimeline.map((item, idx) => (
                  <tr key={idx} className="hover:bg-purple-50/30 transition-colors">
                    <td className="px-6 py-3 text-sm text-gray-800 font-medium">{item.type}</td>
                    <td className="px-6 py-3 text-sm text-gray-600">{item.time}</td>
                    <td className="px-6 py-3 text-sm text-gray-600">{item.method}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQs */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden border border-purple-100 mb-8">
          <div className="p-6 md:p-8 border-b border-gray-200 bg-gradient-to-r from-purple-50/30 to-pink-50/30">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center">
                <FaQuestionCircle className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                Frequently Asked Questions
              </h2>
            </div>
          </div>
          <div className="p-6 md:p-8">
            <div className="space-y-4">
              {faqs.map((faq, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <h3 className="font-semibold text-gray-800 text-sm mb-2 flex items-center gap-2">
                    <span className="text-purple-600">❓</span>
                    {faq.question}
                  </h3>
                  <p className="text-gray-600 text-sm ml-6">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-amber-600 rounded-xl p-6 md:p-8 text-white">
          <div className="text-center mb-6">
            <h3 className="text-xl md:text-2xl font-bold mb-2">Need Help With Your Return?</h3>
            <p className="text-purple-100 text-sm">Our customer support team is here to assist you</p>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="mailto:info@skkjewellers.com"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-all"
            >
              <FaEnvelope className="w-4 h-4" />
              info@skkjewellers.com
            </a>
            <a 
              href="tel:+9118001234567"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-all"
            >
              <FaPhoneAlt className="w-4 h-4" />
              +91 92856 68748
            </a>
          </div>
          <div className="text-center mt-6 text-xs text-purple-200">
            Available Monday to Saturday, 10 AM - 7 PM IST
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-6 text-center text-xs text-gray-500">
          <p>Return shipping costs are non-refundable unless the return is due to our error.</p>
          <p className="mt-1">© {new Date().getFullYear()} SKK Jewelry. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default ReturnsPolicy;